<template>
    <ValidationObserver
        v-slot="{ handleSubmit, invalid }"
        tag="form"
        class="form-mention-group"
        @submit.prevent>
        <div class="mb-3">
            <label
                for="mention-group-name"
                class="card__info-label required mb-1">Name</label>

            <ValidationProvider
                v-slot="{ errors }"
                rules="required|max:100"
                name="name">
                <input
                    id="mention-group-name"
                    ref="name"
                    v-model.trim="name"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.length }"
                    placeholder="Channel Strategy Team">

                <MessageValidationError
                    v-if="errors.length"
                    :msg="errors[0]">
                </MessageValidationError>
            </ValidationProvider>

            <small
                v-if="name"
                class="form-mention-group__handle">
                Tag it in a thread with <strong>@{{ name }}</strong>
            </small>
        </div>

        <div class="mb-2">
            <label
                for="mention-group-people"
                class="card__info-label mb-1">Add people</label>

            <Multiselect
                id="mention-group-people"
                :value="null"
                :options="peopleToAdd"
                :internal-search="false"
                :loading="isSearchingUsers"
                :show-labels="false"
                open-direction="bottom"
                label="display_name"
                track-by="uuid"
                placeholder="Search people to add"
                @select="addMember"
                @search-change="searchPeopleOnPause">
                <template #option="{ option }">
                    <span class="form-mention-group__person">
                        <Avatar
                            :image-src="avatarOf(option)"
                            size="1.5rem"
                            class="form-mention-group__avatar">
                        </Avatar>

                        {{ option.display_name }}
                    </span>
                </template>

                <template #noResult>
                    No one matches that name.
                </template>
            </Multiselect>
        </div>

        <p class="card__info-label mb-1">
            Members ({{ members.length }})
        </p>

        <ul
            v-if="members.length"
            class="form-mention-group__members">
            <li
                v-for="member in members"
                :key="member.uuid"
                class="form-mention-group__member">
                <Avatar
                    :image-src="avatarOf(member)"
                    size="1.75rem"
                    class="form-mention-group__avatar">
                </Avatar>

                <span class="form-mention-group__member-name">{{ member.display_name }}</span>

                <button
                    type="button"
                    class="form-mention-group__remove"
                    :aria-label="`Remove ${member.display_name}`"
                    @click="removeMember(member)">
                    <i
                        aria-hidden="true"
                        class="fas fa-times" />
                </button>
            </li>
        </ul>

        <p
            v-else
            class="form-mention-group__no-members">
            No one yet. Add people above.
        </p>

        <MessageValidationError
            v-if="error"
            class="mb-3"
            :msg="error">
        </MessageValidationError>

        <div class="form-mention-group__actions">
            <button
                v-if="mentionGroup.uuid"
                type="button"
                class="btn btn-link text-danger px-0"
                :disabled="isSaving"
                @click="confirmArchive">
                <i
                    aria-hidden="true"
                    class="fas fa-archive mr-1" /> Archive group
            </button>

            <div class="ml-auto d-flex align-items-center">
                <button
                    type="button"
                    class="btn btn-link mr-3"
                    @click="$emit('cancel')">
                    Cancel
                </button>

                <IconButton
                    class="btn-success"
                    icon="fa-save"
                    :label="mentionGroup.uuid ? 'Save Group' : 'Create Group'"
                    :loading="isSaving"
                    :disabled="invalid"
                    @click="handleSubmit(submit)">
                </IconButton>
            </div>
        </div>
    </ValidationObserver>
</template>

<script>
import Multiselect from 'vue-multiselect'
import Swal from 'sweetalert2'
import { debounce, escape } from 'lodash'
import { mapActions, mapState } from 'pinia'
import {
    Avatar, IconButton, MessageValidationError, avatarOf, refusalOf,
} from '@technologyadvice/relay-frontend'
import { useMentionGroupsStore } from '@store/mentionGroups'

const PEOPLE_SEARCH_PAUSE_MS = 300

export default {
    name: 'FormMentionGroup',
    components: {
        Avatar,
        IconButton,
        MessageValidationError,
        Multiselect,
    },
    props: {
        mentionGroup: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            name: this.mentionGroup.name,
            members: [ ...this.mentionGroup.members ],
            isSaving: false,
            error: null,
        }
    },
    computed: {
        ...mapState(useMentionGroupsStore, [ 'mentionableUsers', 'isSearchingUsers' ]),
        peopleToAdd() {
            const memberUuids = new Set(this.members.map(({ uuid }) => uuid))

            return this.mentionableUsers.filter(({ uuid }) => !memberUuids.has(uuid))
        },
    },
    created() {
        this.searchPeopleOnPause = debounce(this.searchPeople, PEOPLE_SEARCH_PAUSE_MS)
        this.searchPeople('')
    },
    mounted() {
        this.$refs.name.focus()
    },
    beforeDestroy() {
        this.searchPeopleOnPause.cancel()
    },
    methods: {
        avatarOf,
        ...mapActions(useMentionGroupsStore, [ 'searchMentionableUsers', 'save', 'archive' ]),
        async searchPeople(search) {
            try {
                await this.searchMentionableUsers(search)
            } catch {
                this.$_toasterDanger('Could not search people. Try again.')
            }
        },
        addMember(person) {
            this.members = [ ...this.members, person ]
                .sort((first, second) => first.display_name.localeCompare(second.display_name))
        },
        removeMember({ uuid }) {
            this.members = this.members.filter(member => member.uuid !== uuid)
        },
        async submit() {
            this.isSaving = true
            this.error = null

            try {
                const saved = await this.save(this.mentionGroup.uuid, {
                    name: this.name,
                    member_user_uuids: this.members.map(({ uuid }) => uuid),
                })

                this.$_toasterSuccess(`Saved @${escape(saved.name)}`)
                this.$emit('saved', saved)
            } catch (error) {
                this.error = refusalOf(error, 'Could not save this group. Try again.')
            } finally {
                this.isSaving = false
            }
        },
        async confirmArchive() {
            const { value: isConfirmed } = await Swal.fire({
                titleText: `Archive @${this.mentionGroup.name}?`,
                text: 'Nobody can tag it until it is restored from Archived groups. '
                    + 'Threads that already tagged it keep showing its name.',
                type: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, archive',
                reverseButtons: true,
            })

            if (!isConfirmed) {
                return
            }

            try {
                await this.archive(this.mentionGroup.uuid)
                this.$_toasterSuccess(`Archived @${escape(this.mentionGroup.name)}`)
                this.$emit('archived')
            } catch {
                this.$_toasterDanger(`Could not archive @${escape(this.mentionGroup.name)}. Try again.`)
            }
        },
    },
}
</script>
